import { ApiRef, createApiRef } from '@backstage/core-plugin-api';

import {
  McpAccessRequest,
  McpAccessRequestCreateRequest,
  McpAccessRequestCreateResponse,
  McpAccessRequestBulkUpdateRequest,
  McpAccessRequestBulkUpdateResponse,
  McpAccessRequestUpdateRequest,
  McpAccessRequestUpdateResponse,
  McpServerCreateRequest,
  McpServerCreateResponse,
  McpServerDetailed,
  McpServerListResponse,
  McpServerNameAvailabilityResponse,
  McpServerUpdateRequest,
  McpServerUpdateResponse,
} from './mcpgarden.objects';

/**
 * MCP Garden API reference for live version
 */
export const mcpGardenApiRef: ApiRef<McpGardenApi> = createApiRef<McpGardenApi>(
  {
    id: 'plugin.aiecosystem-mcpgarden.api',
  },
);

/**
 * API Interface for MCP Garden network communication
 */
export interface McpGardenApi {
  /**
   * GET all MCP servers, following pagination until every page is collected
   * @param limit - Maximum number of MCP servers to return per page
   * @returns The aggregated list of MCP servers
   */
  getMcpServers(limit?: number): Promise<McpServerListResponse>;

  /**
   * GET a single MCP server by its ID
   * @param mcpServerId - UUID of the MCP server to retrieve
   * @throws {MCPServerNotFoundError} When the MCP server does not exist
   * @returns The detailed MCP server record
   */
  getMcpServer(mcpServerId: string): Promise<McpServerDetailed>;

  /**
   * GET Check whether an MCP server name is still available (case-insensitive)
   * @param name - The MCP server name to check
   * @param excludeMcpServerId - Optional UUID to exclude, used when editing
   * @returns Whether the name is available
   */
  checkMcpServerNameAvailability(
    name: string,
    excludeMcpServerId?: string,
  ): Promise<McpServerNameAvailabilityResponse>;

  /**
   * POST Create a new MCP server in the MCP Garden
   * @param request - The onboarding payload
   * @returns The created MCP server ID and its onboarding status
   */
  createMcpServer(
    request: McpServerCreateRequest,
  ): Promise<McpServerCreateResponse>;

  /**
   * PUT Update an existing MCP server
   * @param mcpServerId - UUID of the MCP server to update
   * @param request - The fields to update
   * @returns The updated MCP server and the list of changed fields
   */
  updateMcpServer(
    mcpServerId: string,
    request: McpServerUpdateRequest,
  ): Promise<McpServerUpdateResponse>;

  /**
   * GET all access requests for a specific MCP server
   * @param mcpServerId - UUID of the MCP server
   * @returns Array of access requests
   */
  getMcpAccessRequests(mcpServerId: string): Promise<McpAccessRequest[]>;

  /**
   * POST Create a new access request for an MCP server
   * @param mcpServerId - UUID of the MCP server
   * @param request - The access request payload
   * @returns The created access request
   */
  createMcpAccessRequest(
    mcpServerId: string,
    request: McpAccessRequestCreateRequest,
  ): Promise<McpAccessRequestCreateResponse>;

  /**
   * PUT update one or multiple MCP access requests
   * @param mcpServerId - UUID of the MCP server
   * @param request - Request IDs, decision and optional notes
   * @returns Aggregated bulk update result
   */
  updateMcpAccessRequests(
    mcpServerId: string,
    request: McpAccessRequestBulkUpdateRequest,
  ): Promise<McpAccessRequestBulkUpdateResponse>;

  /**
   * PUT unsubscribe from an MCP server
   * @param mcpServerId - UUID of the MCP server
   * @param requestId - UUID of the access request to unsubscribe
   * @param request - The access request update payload
   * @returns The updated access request
   */
  unsubscribeFromMcpServer(
    mcpServerId: string,
    requestId: string,
    request: McpAccessRequestUpdateRequest,
  ): Promise<McpAccessRequestUpdateResponse>;

  /**
   * POST Deprecate an MCP server — either immediately or after a deprecation phase
   * @param mcpServerId - UUID of the MCP server being deprecated
   * @param request - The confirmation name, deprecation duration, reason and suggested alternatives
   * @returns The deprecation status with start/completion timestamps
   */
  deprecateMcpServer(
    mcpServerId: string,
    formData: {
      mcpServerName: string;
      deprecationDurationDays: number | null;
      deprecationReason: string;
      alternatives: string;
    },
  ): Promise<{
    mcpServerId: string;
    mcpServerName: string;
    status: string;
    deprecationStartedAt: string;
    deprecationCompletesAt: string;
    message: string;
  }>;
}
